import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ActionBar.scss";
import { Card, Icon, IconName } from "@repo/ping-design";
import {
  autoUpdate,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import cx from "classnames";
import { isNumber } from "lodash";
import {
  LAYER_GOOGLE,
  LAYER_HEATMAP,
  OverlayLayer,
  OverlayLayerSubItem,
} from "@repo/ping-react-js";
import { ButtonConfig } from "../../../ts-types/mapsTypes";
import SecondaryActionBar, {
  SecondaryActionBarButton,
} from "./SecondaryActionBar";

type ActionBarProps = {
  pageState: string;
  style?: React.CSSProperties;
  buttonConfigs: ButtonConfig[];

  enabledLayer: OverlayLayer | OverlayLayerSubItem | null;
  lineMeasurementMode: boolean;
  dtcMode: boolean;
  areaMeasurementMode: boolean;
  elevationMeasurementMode: boolean;
  elevationProfileMode: boolean;
  creatingShapeFilterMode: boolean;
  finalizingShapeFilterMode: boolean;
  isShapeFilterActive: boolean;
  filters: Record<string, string | string[] | undefined>;
  attach: number | null;
  layerLimit: number | null;
  windowWidth: number;
  renderActiveContent: (
    activeButton: string | null,
    context: any,
    updateButtonConfig: (
      buttonId: string,
      defaultLabel: string,
      selection: { label?: string; iconName?: IconName },
    ) => void,
  ) => React.ReactNode;

  renderSecondaryBar?: (
    activeButton: string | null,
    setActiveSecondaryButton?: (id: string) => void,
    registerResetCallback?: (resetFn: () => void) => void,
  ) => SecondaryActionBarButton[] | null;

  onSecondaryBarVisibilityChange?: (isVisible: boolean) => void;

  defaultSecondaryButton?: string | null;
  children?: React.ReactNode;
};

const ActionBar = memo(
  ({
    style,
    pageState,
    buttonConfigs: initialButtonConfigs,
    enabledLayer,
    lineMeasurementMode,
    dtcMode,
    areaMeasurementMode,
    elevationMeasurementMode,
    elevationProfileMode,
    creatingShapeFilterMode,
    finalizingShapeFilterMode,
    isShapeFilterActive,
    filters,
    attach,
    layerLimit,
    windowWidth,
    renderActiveContent,
    renderSecondaryBar,
    onSecondaryBarVisibilityChange,
    defaultSecondaryButton,
    children,
  }: ActionBarProps) => {
    const [activeButton, setActiveButton] = useState<string | null>(null);
    const [buttonConfigs, setButtonConfigs] =
      useState<ButtonConfig[]>(initialButtonConfigs);
    const [activeSecondaryButton, setActiveSecondaryButton] = useState<
      string | null
    >(null);
    const [secondaryButtonRefs, setSecondaryButtonRefs] = useState<
      Map<string, HTMLButtonElement>
    >(new Map());
    const secondaryTabResetRef = useRef<(() => void) | null>(null);
    const prevActiveButtonRef = useRef<string | null>(null);

    useEffect(() => {
      setButtonConfigs(initialButtonConfigs);
    }, [initialButtonConfigs]);

    useEffect(() => {
      if (prevActiveButtonRef.current !== activeButton) {
        setActiveSecondaryButton(null);
        if (secondaryTabResetRef.current) {
          secondaryTabResetRef.current();
        }
        prevActiveButtonRef.current = activeButton;
      }
    }, [activeButton]);

    const isUsingMeasurementTool = useMemo(
      () =>
        dtcMode ||
        lineMeasurementMode ||
        areaMeasurementMode ||
        elevationMeasurementMode ||
        elevationProfileMode,
      [
        dtcMode,
        lineMeasurementMode,
        areaMeasurementMode,
        elevationMeasurementMode,
        elevationProfileMode,
      ],
    );

    const updateButtonConfig = useCallback(
      (
        buttonId: string,
        defaultLabel: string,
        selection: { label?: string; iconName?: IconName },
      ) => {
        setButtonConfigs((prev) =>
          prev.map((config) => {
            if (config.id === buttonId) {
              return {
                ...config,
                label: selection.label || defaultLabel,
                iconName: selection.iconName || config.iconName,
              };
            }
            return config;
          }),
        );
      },
      [],
    );

    const getCrossAxisOffset = useCallback(() => {
      const SMALL_SCREEN_BREAKPOINT = 1600;
      if (
        windowWidth <= SMALL_SCREEN_BREAKPOINT &&
        activeButton === "filters"
      ) {
        return 150;
      }
      return 0;
    }, [activeButton, windowWidth]);

    const crossAxisOffset = getCrossAxisOffset();

    const registerResetCallback = useCallback((resetFn: () => void) => {
      secondaryTabResetRef.current = resetFn;
    }, []);

    const secondaryBarButtons = useMemo(() => {
      if (!activeButton || !renderSecondaryBar) return null;
      return renderSecondaryBar(
        activeButton,
        setActiveSecondaryButton,
        registerResetCallback,
      );
    }, [activeButton, renderSecondaryBar, registerResetCallback]);

    const hasSecondaryBar =
      secondaryBarButtons !== null && secondaryBarButtons.length > 0;

    useEffect(() => {
      onSecondaryBarVisibilityChange?.(hasSecondaryBar);
    }, [hasSecondaryBar, onSecondaryBarVisibilityChange]);

    const floatingReference = useMemo(() => {
      const effectiveSecondaryButton =
        activeSecondaryButton || defaultSecondaryButton;
      if (
        effectiveSecondaryButton &&
        secondaryButtonRefs.has(effectiveSecondaryButton)
      ) {
        return secondaryButtonRefs.get(effectiveSecondaryButton);
      }
      return undefined;
    }, [activeSecondaryButton, defaultSecondaryButton, secondaryButtonRefs]);

    const { refs, floatingStyles, context } = useFloating({
      open: !!activeButton,
      onOpenChange: (open) => {
        if (!open) {
          if (activeButton === "overlays") {
            setButtonConfigs((prev) =>
              prev.map((config) => {
                if (config.id === "overlays") {
                  return {
                    ...config,
                    iconName: "ChevronUp",
                  };
                }
                return config;
              }),
            );
          }
          setActiveButton(null);
          setActiveSecondaryButton(null);
          if (secondaryTabResetRef.current) {
            secondaryTabResetRef.current();
          }
        }
      },
      placement: "top",
      middleware: [
        offset({
          mainAxis: 10,
          crossAxis: crossAxisOffset,
        }),
        shift(),
      ],
      whileElementsMounted: autoUpdate,
    });

    useEffect(() => {
      if (floatingReference) {
        refs.setReference(floatingReference);
      }
    }, [floatingReference, refs]);

    const handleSecondaryButtonRef = useCallback(
      (buttonId: string, ref: HTMLButtonElement | null) => {
        setSecondaryButtonRefs((prev) => {
          const newMap = new Map(prev);
          if (ref) {
            newMap.set(buttonId, ref);
          } else {
            newMap.delete(buttonId);
          }
          return newMap;
        });
      },
      [],
    );

    const click = useClick(context);
    const dismiss = useDismiss(context, {
      outsidePress: (event) => {
        const target = event.target as HTMLElement;
        const secondaryBar = target.closest(".SecondaryActionBar");
        const clearButton = target.closest(".PingMapWorkspaceClearButton, .PingVisionClearButton");
        return !secondaryBar && !clearButton;
      },
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([
      click,
      dismiss,
    ]);

    useEffect(() => {
      if (!activeButton || !context.update) return;

      context.update();
      const intervalId = setInterval(() => {
        context.update();
      }, 100);

      const timeoutId = setTimeout(() => {
        clearInterval(intervalId);
      }, 500);

      return () => {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      };
    }, [activeButton, context]);

    useEffect(() => {
      if (context && context.update) {
        context.update();
      }
    }, [windowWidth, context]);

    useEffect(() => {
      if (enabledLayer === LAYER_GOOGLE) {
        const overlayButton = buttonConfigs.find(
          (btn) => btn.id === "overlays",
        );
        if (overlayButton && overlayButton.label !== "No overlay") {
          updateButtonConfig("overlays", "No overlay", {});
        }
      }
    }, [enabledLayer, buttonConfigs, updateButtonConfig]);

    useEffect(() => {
      if (!isUsingMeasurementTool) {
        const measurementButton = buttonConfigs.find(
          (btn) => btn.id === "measurements",
        );
        if (measurementButton && measurementButton.label !== "Measure") {
          updateButtonConfig("measurements", "Measure", {
            iconName: "DraftingCompass",
          });
        }
      }
    }, [isUsingMeasurementTool, buttonConfigs, updateButtonConfig]);

    useEffect(() => {
      if (
        !isShapeFilterActive &&
        !creatingShapeFilterMode &&
        !finalizingShapeFilterMode
      ) {
        const shapeFilterButton = buttonConfigs.find(
          (btn) => btn.id === "shape-filters",
        );
        if (shapeFilterButton && shapeFilterButton.label !== "Shape Filter") {
          updateButtonConfig("shape-filters", "Shape Filter", {
            iconName: "Shapes",
          });
        }
      }
    }, [
      isShapeFilterActive,
      buttonConfigs,
      creatingShapeFilterMode,
      finalizingShapeFilterMode,
      updateButtonConfig,
    ]);

    const filterAmount = useMemo(() => {
      const usingLayers =
        isNumber(attach) && isNumber(layerLimit) && layerLimit > 0;

      const fArray = Object.entries(filters);
      const layer = usingLayers ? [["layer", `${layerLimit}:::${attach}`]] : [];
      const filtersAsArray = [...layer, ...fArray];

      const expandedFilters = filtersAsArray.flatMap(([key, value]) => {
        if (Array.isArray(value)) {
          return value.map((singleValue) => [key, singleValue]);
        }
        return [[key, value]];
      });

      return expandedFilters.length;
    }, [attach, filters, layerLimit]);

    return (
      <div
        style={{ ...style, zIndex: pageState === "map" ? 20 : 15 }}
        className="ActionBar"
      >
        {secondaryBarButtons && secondaryBarButtons.length > 0 && (
          <SecondaryActionBar
            buttons={secondaryBarButtons}
            onButtonRef={handleSecondaryButtonRef}
          />
        )}
        <Card className="ActionBar__Card">
          {buttonConfigs.map((btn) => (
            <button
              key={btn.id}
              disabled={btn.disabled}
              className={cx("ActionBar__Button", {
                "ActionBar__Button--Active":
                  activeButton === btn.id ||
                  (isUsingMeasurementTool && btn.id === "measurements") ||
                  ((isShapeFilterActive ||
                    creatingShapeFilterMode ||
                    finalizingShapeFilterMode) &&
                    btn.id === "shape-filters") ||
                  (enabledLayer &&
                    enabledLayer !== LAYER_GOOGLE &&
                    enabledLayer !== LAYER_HEATMAP &&
                    btn.id === "overlays"),
              })}
              onClick={() => {
                if (btn.id === "overlays") {
                  setButtonConfigs((prev) =>
                    prev.map((config) => {
                      if (config.id === "overlays") {
                        return {
                          ...config,
                          iconName:
                            config.iconName === "ChevronUp"
                              ? "ChevronDown"
                              : "ChevronUp",
                        };
                      }
                      return config;
                    }),
                  );
                }
                setActiveButton((prev) => (prev === btn.id ? null : btn.id));
                setActiveSecondaryButton(null);
                setSecondaryButtonRefs(new Map());
              }}
              ref={activeButton === btn.id ? refs.setReference : null}
              {...(activeButton === btn.id ? getReferenceProps() : {})}
            >
              {btn.iconOrientation === "left" && (
                <Icon iconName={btn.iconName} size={16} />
              )}
              <span className="ActionBar__ButtonLabel">
                {enabledLayer &&
                enabledLayer !== LAYER_GOOGLE &&
                enabledLayer !== LAYER_HEATMAP &&
                btn.id === "overlays"
                  ? enabledLayer.label
                  : btn.label}
              </span>
              {btn.iconOrientation === "right" && (
                <Icon iconName={btn.iconName} size={16} />
              )}
              {btn.id === "filters" && filterAmount > 0 && (
                <div className="ActionBar__FilterBadge">{filterAmount}</div>
              )}
            </button>
          ))}
          {children}
        </Card>
        {activeButton && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="ActionBar__Dropup"
            {...getFloatingProps()}
          >
            <Card className="ActionBar__DropupCard">
              {renderActiveContent(activeButton, context, updateButtonConfig)}
            </Card>
          </div>
        )}
      </div>
    );
  },
);

export default ActionBar;
